import { useRef, useState, useCallback, useEffect } from 'react'
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react'
import './resizableImage.css'

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se'

export function ResizableImageView({ node, updateAttributes, selected }: NodeViewProps) {
  const imageRef = useRef<HTMLImageElement>(null)
  const [isResizing, setIsResizing] = useState(false)
  const [showSizeIndicator, setShowSizeIndicator] = useState(false)
  const startSize = useRef({ width: 0, height: 0 })
  const startPos = useRef({ x: 0, y: 0 })
  const resizeDirection = useRef<ResizeDirection | null>(null)
  const aspectRatio = useRef(1)

  const { src, alt, width, height, alignment, float } = node.attrs

  // Build class names for wrapper
  const wrapperClasses = [
    'resizable-image-wrapper',
    float !== 'none' ? `float-${float}` : `align-${alignment}`,
    selected ? 'selected' : '',
    isResizing ? 'resizing' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, direction: ResizeDirection) => {
      e.preventDefault()
      e.stopPropagation()

      const img = imageRef.current
      if (!img) return

      setIsResizing(true)
      setShowSizeIndicator(true)
      resizeDirection.current = direction

      startSize.current = {
        width: img.offsetWidth,
        height: img.offsetHeight,
      }
      startPos.current = { x: e.clientX, y: e.clientY }
      aspectRatio.current = img.offsetWidth / img.offsetHeight
    },
    []
  )

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - startPos.current.x
      const dy = e.clientY - startPos.current.y
      const dir = resizeDirection.current

      let newWidth = startSize.current.width
      let newHeight = startSize.current.height

      // Calculate new size based on resize direction
      if (dir?.includes('e')) {
        newWidth = startSize.current.width + dx
      }
      if (dir?.includes('w')) {
        newWidth = startSize.current.width - dx
      }
      if (dir?.includes('s')) {
        newHeight = startSize.current.height + dy
      }
      if (dir?.includes('n')) {
        newHeight = startSize.current.height - dy
      }

      // Maintain aspect ratio for corner handles
      if (dir && dir.length === 2) {
        // Use the larger dimension change to maintain aspect ratio
        const widthDelta = Math.abs(newWidth - startSize.current.width)
        const heightDelta = Math.abs(newHeight - startSize.current.height)

        if (widthDelta > heightDelta) {
          newHeight = newWidth / aspectRatio.current
        } else {
          newWidth = newHeight * aspectRatio.current
        }
      }

      // Minimum size constraints
      newWidth = Math.max(50, newWidth)
      newHeight = Math.max(50, newHeight)

      // Maximum size constraints (don't exceed container)
      newWidth = Math.min(800, newWidth)

      updateAttributes({
        width: Math.round(newWidth),
        height: Math.round(newHeight),
      })
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      setShowSizeIndicator(false)
      resizeDirection.current = null
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, updateAttributes])

  // Get current display dimensions
  const displayWidth = width || imageRef.current?.naturalWidth || 'auto'
  const displayHeight = height || imageRef.current?.naturalHeight || 'auto'

  return (
    <NodeViewWrapper className={wrapperClasses} data-drag-handle>
      <img
        ref={imageRef}
        src={src}
        alt={alt || ''}
        width={width || undefined}
        height={height || undefined}
        draggable={false}
        style={{
          width: width ? `${width}px` : undefined,
          height: height ? `${height}px` : undefined,
        }}
      />

      {/* Resize handles - only show when selected */}
      {selected && (
        <div className="resize-handles">
          {/* Corner handles */}
          <div
            className="resize-handle nw"
            onMouseDown={(e) => handleMouseDown(e, 'nw')}
          />
          <div
            className="resize-handle ne"
            onMouseDown={(e) => handleMouseDown(e, 'ne')}
          />
          <div
            className="resize-handle sw"
            onMouseDown={(e) => handleMouseDown(e, 'sw')}
          />
          <div
            className="resize-handle se"
            onMouseDown={(e) => handleMouseDown(e, 'se')}
          />

          {/* Edge handles */}
          <div
            className="resize-handle n"
            onMouseDown={(e) => handleMouseDown(e, 'n')}
          />
          <div
            className="resize-handle s"
            onMouseDown={(e) => handleMouseDown(e, 's')}
          />
          <div
            className="resize-handle e"
            onMouseDown={(e) => handleMouseDown(e, 'e')}
          />
          <div
            className="resize-handle w"
            onMouseDown={(e) => handleMouseDown(e, 'w')}
          />
        </div>
      )}

      {/* Size indicator during resize */}
      {showSizeIndicator && (
        <div className="size-indicator">
          {displayWidth} x {displayHeight}
        </div>
      )}
    </NodeViewWrapper>
  )
}
